import html2canvas from 'html2canvas';
import katex from 'katex';

const MATH_IMAGE_SCALE = 4;

export type MathImageData = {
  dataUrl: string;
  width: number;
  height: number;
};

/**
 * Render a LaTeX string to a PNG data URL for embedding in jsPDF.
 * Uses KaTeX HTML output rendered into the live DOM (where KaTeX fonts/CSS
 * are already loaded), captured via html2canvas.
 */
export async function renderMathToImage(
  latex: string,
  options: {
    displayMode?: boolean;
    fontSize?: number;
    color?: string;
  } = {},
): Promise<MathImageData | null> {
  const { displayMode = false, fontSize = 16, color } = options;

  let html: string;
  try {
    html = katex.renderToString(latex, {
      throwOnError: false,
      displayMode,
      output: 'html',
    });
  } catch {
    return null;
  }

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.fontSize = `${fontSize}px`;
  container.style.lineHeight = '1.2';
  container.style.background = 'transparent';
  if (color) container.style.color = color;
  container.innerHTML = html;
  document.body.appendChild(container);

  await document.fonts.ready;

  const rect = container.getBoundingClientRect();
  const width = Math.ceil(rect.width) + 2;
  const height = Math.ceil(rect.height) + 2;

  if (width <= 2 || height <= 2) {
    document.body.removeChild(container);
    return null;
  }

  try {
    const canvas = await html2canvas(container, {
      scale: MATH_IMAGE_SCALE,
      backgroundColor: null,
      logging: false,
      useCORS: true,
    });

    const dataUrl = canvas.toDataURL('image/png');
    return { dataUrl, width, height };
  } catch {
    return null;
  } finally {
    document.body.removeChild(container);
  }
}

export function resetMathCache() {
  // reserved for future caching if needed
}
